import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CheckCircle2,
  Home,
  Loader2,
  MapPin,
  Plane,
  Send,
  X,
} from "lucide-react";

import { api } from "../lib/api";
import MapPicker from "./MapPicker";

type Status = "home" | "relocated" | "in_transit" | "unknown";

interface Props {
  oracleId: string;
  /** Current status pill — used for context only. */
  currentStatus: string;
  /** Pre-fill values when "At home" is selected. */
  homeCountry: string | null;
  homeCity: string | null;
  homeAddress: string | null;
  homeLat?: number | null;
  homeLng?: number | null;
}

const PRESETS: { id: Status; label: string; icon: React.ReactNode; tone: string }[] = [
  {
    id: "home",
    label: "At home",
    icon: <Home size={14} />,
    tone: "emerald",
  },
  {
    id: "relocated",
    label: "Relocated",
    icon: <MapPin size={14} />,
    tone: "amber",
  },
  {
    id: "in_transit",
    label: "In transit",
    icon: <Plane size={14} />,
    tone: "sky",
  },
  {
    id: "unknown",
    label: "Other / unknown",
    icon: null,
    tone: "slate",
  },
];

/**
 * Compact one-click check-in panel for HR admins. Mirrors the public
 * WhatsApp check-in form, but lives at the top of the employee detail
 * page so the admin doesn't have to open the full edit modal.
 *
 * UX:
 *   - "At home" submits instantly (uses the saved home address).
 *   - Other statuses expand a small inline form for country/city.
 *   - Optional note + map picker.
 *   - On success, a transient confirmation chip appears for 3s.
 */
export default function QuickCheckIn({
  oracleId,
  currentStatus,
  homeCountry,
  homeCity,
  homeAddress,
  homeLat,
  homeLng,
}: Props) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status | null>(null);
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [success, setSuccess] = useState(false);

  function reset() {
    setStatus(null);
    setCountry("");
    setCity("");
    setAddress("");
    setNote("");
    setLat(null);
    setLng(null);
  }

  const submit = useMutation({
    mutationFn: (override?: Partial<{
      status: Status;
      country: string | null;
      city: string | null;
      address: string | null;
      lat: number | null;
      lng: number | null;
    }>) => {
      const body = {
        status: override?.status ?? status,
        country: override?.country ?? (country || null),
        city: override?.city ?? (city || null),
        address: override?.address ?? (address || null),
        lat: override?.lat ?? lat,
        lng: override?.lng ?? lng,
        note: note || null,
      };
      return api.post(
        `/api/employees/${encodeURIComponent(oracleId)}/checkin`,
        body,
      );
    },
    onSuccess: () => {
      setSuccess(true);
      qc.invalidateQueries({ queryKey: ["employee", oracleId] });
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["stats"] });
      setTimeout(() => {
        setSuccess(false);
        reset();
      }, 2500);
    },
  });

  function pickStatus(s: Status) {
    if (s === "home") {
      // 1-click for home — submit immediately using the saved home info.
      submit.mutate({
        status: "home",
        country: homeCountry,
        city: homeCity,
        address: homeAddress,
        lat: homeLat ?? null,
        lng: homeLng ?? null,
      });
      return;
    }
    setStatus(s);
  }

  const canSubmit =
    status !== null &&
    !submit.isPending &&
    (country.trim() !== "" || (lat != null && lng != null));

  return (
    <div className="rounded-xl ring-1 ring-[var(--color-border)] bg-[var(--color-surface)]/60 p-3 sm:p-4 space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-sky-300 font-medium">
            Update location
          </span>
          <span className="text-[11px] text-slate-500">
            current: <span className="text-slate-300">{currentStatus}</span>
          </span>
        </div>
        {success && (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-200 bg-emerald-500/10 ring-1 ring-emerald-500/30 rounded-full px-2.5 py-0.5">
            <CheckCircle2 size={12} /> Check-in recorded
          </span>
        )}
        {submit.isError && (
          <span className="text-xs text-red-300">
            {(submit.error as Error).message || "Failed"}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PRESETS.map((p) => {
          const isActive = status === p.id;
          const isLoading =
            submit.isPending && submit.variables?.status === p.id;
          return (
            <button
              key={p.id}
              type="button"
              disabled={submit.isPending}
              onClick={() => pickStatus(p.id)}
              className={
                "rounded-lg px-3 py-2.5 text-sm ring-1 transition inline-flex items-center justify-center gap-1.5 disabled:opacity-50 " +
                (isActive
                  ? "bg-sky-500/20 text-sky-100 ring-sky-400/50"
                  : "bg-slate-900/40 text-slate-200 ring-slate-700 hover:bg-slate-800/70")
              }
            >
              {isLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                p.icon
              )}
              {p.label}
            </button>
          );
        })}
      </div>

      {status && status !== "home" && (
        <div className="space-y-2 pt-1 border-t border-slate-800">
          <div className="grid grid-cols-2 gap-2">
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="Country *"
              className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 outline-none focus:ring-sky-500"
            />
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City"
              className="rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 outline-none focus:ring-sky-500"
            />
          </div>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Address (optional)"
            className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 outline-none focus:ring-sky-500"
          />
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            placeholder="Note (optional)"
            className="w-full rounded-md bg-slate-900/80 ring-1 ring-slate-700 px-3 py-2 text-sm text-slate-100 outline-none focus:ring-sky-500 resize-none"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs">
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md ring-1 ring-slate-700 text-slate-200 hover:bg-slate-800"
              >
                <MapPin size={12} />
                {lat != null && lng != null ? "Edit pin" : "Pick on map"}
              </button>
              {lat != null && lng != null && (
                <span className="text-slate-400 tabular-nums">
                  {lat.toFixed(4)}, {lng.toFixed(4)}
                </span>
              )}
              <button
                type="button"
                onClick={reset}
                className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-slate-400 hover:text-slate-200"
                title="Cancel"
              >
                <X size={12} />
              </button>
            </div>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => submit.mutate(undefined)}
              className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md bg-sky-500 hover:bg-sky-400 disabled:opacity-50 text-white text-sm font-medium"
            >
              {submit.isPending ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Send size={13} />
              )}
              Submit
            </button>
          </div>
        </div>
      )}

      {pickerOpen && (
        <MapPicker
          lat={lat}
          lng={lng}
          onClose={() => setPickerOpen(false)}
          onPick={(la, ln) => {
            setLat(la);
            setLng(ln);
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
}
